// The tour's step list and DOM lookups (design guide.jsx), as plain
// functions the tour store and its tests share.

export const ALL = 'all';

// One flat list of steps for the whole tour ('all') or one chapter. Each
// step knows its chapter, its place in it and the chapter's length.
export const flattenSteps = (chapters, chapterId = ALL) => {
  const list =
    chapterId && chapterId !== ALL
      ? chapters.filter((chapter) => chapter.id === chapterId)
      : chapters;
  return list.reduce(
    (acc, chapter) =>
      acc.concat(
        chapter.steps.map((step, i) => ({
          ...step,
          chapter,
          chapterIndex: i,
          chapterCount: chapter.steps.length,
        }))
      ),
    []
  );
};

// "3 of 7" counts within the chapter when only one chapter runs, and across
// the whole tour otherwise.
export const counterLabel = (steps, index, chapterId) => {
  const step = steps[index];
  if (!step) return '';
  return chapterId && chapterId !== ALL
    ? `${step.chapterIndex + 1} of ${step.chapterCount}`
    : `${index + 1} of ${steps.length}`;
};

export const progressPercent = (steps, index) =>
  steps.length ? ((index + 1) / steps.length) * 100 : 0;

export const tourSelector = (id) => `[data-tour="${id}"]`;

// Laid out and not hidden.
export const isShown = (el) => {
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

export const findShown = (doc, id) => {
  const list = doc.querySelectorAll(tourSelector(id));
  for (let i = 0; i < list.length; i += 1) {
    if (isShown(list[i])) return list[i];
  }
  return null;
};

// The elements a step lights up right now: the first target that shows, or
// for a union step every target that shows. [] when none do.
export const resolveStepTargets = (doc, step) => {
  if (step.union) {
    return step.targets.map((id) => findShown(doc, id)).filter(Boolean);
  }
  for (let i = 0; i < step.targets.length; i += 1) {
    const el = findShown(doc, step.targets[i]);
    if (el) return [el];
  }
  return [];
};

// After a missing target: keep going the way the user was going. -1 means
// we ran off either end.
export const skipIndex = (index, dir, length) => {
  const next = index + dir;
  return next >= 0 && next < length ? next : -1;
};

// Bring a target into view inside its scrolling ancestors, without
// scrollIntoView (which can scroll the whole page and fight the overlay).
export const ensureVisible = (el) => {
  const view = el.ownerDocument.defaultView;
  let parent = el.parentElement;
  while (parent && parent !== el.ownerDocument.body) {
    const style = view.getComputedStyle(parent);
    if (
      /(auto|scroll)/.test(style.overflowY) &&
      parent.scrollHeight > parent.clientHeight + 1
    ) {
      const outer = parent.getBoundingClientRect();
      const inner = el.getBoundingClientRect();
      if (inner.top < outer.top + 16 || inner.bottom > outer.bottom - 16) {
        const offset =
          inner.height > outer.height - 48
            ? 24
            : (outer.height - inner.height) / 2;
        // eslint-disable-next-line no-param-reassign
        parent.scrollTop = Math.max(
          0,
          parent.scrollTop + (inner.top - outer.top) - offset
        );
      }
    }
    parent = parent.parentElement;
  }
};
